import { HttpClient } from "@angular/common/http";
import { Injectable, OnDestroy } from "@angular/core";
import { Store } from "@ngrx/store";
import { Mutex } from "async-mutex";
import moment from "moment";
import { IMqttMessage } from "ngx-mqtt";
import {
  BehaviorSubject,
  Observable,
  of,
  Subscription,
  throwError,
} from "rxjs";
import {
  debounceTime,
  filter,
  map,
  switchMap,
  withLatestFrom,
} from "rxjs/operators";
import { environment } from "src/environments/environment";
import {
  AccountingEntities,
  AccountingRequest,
  AccountingRequestFile,
  AccountingResult,
} from "src/model/class/general classes/accounting-entities";
import { AccountingNote } from "src/model/class/general classes/accounting-note";
import { AppReserved } from "src/model/class/general classes/app-reserved";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { DataStorage } from "src/model/class/general classes/data-storage";
import { GenericApiData } from "src/components/shared-components/class/general-classes/generic-api-data";
import { GenericIDs } from "src/model/class/general classes/generic-ids";
import { ApiErrorType } from "src/model/enum/enumApiErrorType";
import { GenericOperationKind } from "src/model/enum/enumGenericOperationKind";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { iEvent } from "src/components/shared-components/interfaces/iEvent";
import { iMqttHandling } from "src/model/interface/iMqtt-handling";
import { isUserAuthenticated } from "src/state/selectors/authentication.selectors";
import { EventService } from "./event.service";
import { LocalStorageService } from "./local-storage.service";
import { LoggerService } from "./logger.service";
import { MqttClientService } from "./mqtt-client.service";
import { MomentFormats } from "src/model/class/general classes/moment-formats";

@Injectable({
  providedIn: "root",
})
export class AccountingService
  extends DataStorage
  implements OnDestroy, iEvent, iMqttHandling {
  private _logContext: string = "Accounting.service";
  private _accountingEntitiesStore: GenericApiData = null;
  private _accountingEntitiesSubject: BehaviorSubject<
    Array<AccountingEntities>
  > = new BehaviorSubject<Array<AccountingEntities>>(null);

  private _isErrorNotSupported: boolean = false;
  _eventSubscriptions: Array<Subscription> = null;

  /**Flag property; if the service ha foud an error in the last API call needed for get the account-entities-list from the server, it is set to TRUE;
   * otherwise, it is set to FALSE.
   */
  private _isErrorInGetAccountingEntitiesListAPIRequest: boolean = false;

  _mqttAddressSubscriptionsMap: Map<string, Subscription> = null;

  public needToInitialize: boolean = true;

  public readonly accountingEntitiesList =
    this._accountingEntitiesSubject.asObservable();

  private mutex = new Mutex();

  get isErrorInGetAccountingEntitiesListAPIRequest(): boolean {
    return this._isErrorInGetAccountingEntitiesListAPIRequest;
  }

  get isErrorNotSupported(): boolean {
    return this._isErrorNotSupported;
  }

  constructor(
    private _storageService: LocalStorageService,
    private _http: HttpClient,
    private _mqttService: MqttClientService,
    private _eventService: EventService,
    private _loggerService: LoggerService,
    private _store: Store
  ) {
    super();

    this._mqttAddressSubscriptionsMap = new Map<string, Subscription>();

    /** Subscrive event from Event Service */
    this.subscribeEvents();
    this.subscribeMQTTAddresses();
  }

  /** Destroy event service */
  ngOnDestroy() {
    this._accountingEntitiesSubject.next(null);
    this._accountingEntitiesSubject.complete();

    this.unsubscribeEvents();

    this.unsubscribeToMQTTAddresses();
  }

  //#region Subscribe to event-service observable
  subscribeEvents(): void {
    this._eventSubscriptions = new Array<Subscription>();

    this._eventSubscriptions.push(
      this._eventService.OnComeBackOnline.pipe(
        withLatestFrom(this._store.select(isUserAuthenticated)),
        filter((combinedArray: Array<any>) => combinedArray[1]),
        map((combinedArray: Array<any>) => combinedArray[0])
      ).subscribe(() => {
        this.initializeAccountingEntitiesList();
      })
    );

    /** Operation performed on logout */
    this._eventSubscriptions.push(
      this._eventService.OnMakeLogout.subscribe(async () => {
        this._storageService.remove(AppSettings.KEY_ACCOUNTING_ENTITIES);

        await this.unsubscribeToMQTTAddresses();

        this._accountingEntitiesStore = null;
        this._accountingEntitiesSubject.next(null);
        this.needToInitialize = true;
      })
    );

    this._eventSubscriptions.push(
      this._eventService.OnLoginPlantDid.subscribe(() => {
        this.subscribeMQTTAddresses();
      })
    );
  }

  unsubscribeEvents(): void {
    for (let sub of this._eventSubscriptions) {
      sub.unsubscribe();
    }
  }
  //#endregion

  /**
   *  Initialize public observable list trasmission: it should be call in the costructor of all component/page that you need.
   */
  public Init() {
    if (this.needToInitialize) {
      this.initializeAccountingEntitiesList();

      this.needToInitialize = false;
    }
  }

  /**
   * Reload service data (es. in case of error)
   */
  public reloadServiceData() {
    this.needToInitialize = true;

    this.Init();
  }

  /**
   * Initialize accounting entities list: first it read from storage (via getFromStorage() function)
   * then if storage read failed it tries to retrive data from API
   */
  private initializeAccountingEntitiesList() {
    this.getFromStorage().then((resultReadStorage: GenericApiData) => {
      if (!resultReadStorage) {
        this.GetAccountingEntitites("");
      } else {
        this.GetAccountingEntitiesByLastModify(resultReadStorage);
      }
    });
  }

  //#region Mqtt event

  async subscribeMQTTAddresses() {
    let mqttAddress: string = "";
    let mqttAddressSubcription: Subscription = null;
    let returnedObservable: Observable<IMqttMessage> = null;

    mqttAddress =
      AppSettings.INSTANCE_GUID_VIRTUAL_VARIABLE +
      "_" +
      AppReserved.RESERVED_DB_ACCOUNTING_ENTITIES_CHANGED;

    returnedObservable = await this._mqttService.subscribeTopic(mqttAddress);

    //this._mqttAddressSubscriptions.push(
    mqttAddressSubcription = returnedObservable
      .pipe(
        filter(() => this._accountingEntitiesStore !== null),
        map((mqttMessage: IMqttMessage) => {
          let message = JSON.parse(
            mqttMessage.payload.toString()
          ).Message.Value.split("|");
          message.push(
            JSON.parse(mqttMessage.payload.toString()).Message.ClientID
          );
          return message;
        }),
        map(async (message: Array<string>) => {
          /** map operator allow to make parallel execution without cancelling event: when this fired multiple times
           * it perform execeution without waiting previous execution complete
           */
          if (message.length === 3) {
            const release = await this.mutex.acquire();

            try {
              const operationKind = parseInt(message[0]);
              const idAccountingEntity = parseInt(message[1]);
              const clientID = message[2];
              /** Finding operation kind: Insert, Update, Delete */
              if (operationKind === GenericOperationKind.INSERT) {
                const indexUpdateAccountingEntities =
                  this._accountingEntitiesStore.data.findIndex(
                    (accountingEntity: AccountingEntities) =>
                      accountingEntity.id === idAccountingEntity
                  );
                if (indexUpdateAccountingEntities < 0) {
                  /** Retrive insert object from api */
                  const insertAccountingEntity: AccountingEntities =
                    await this.GetAccountingEntityByID(
                      idAccountingEntity
                    ).catch((error) => {
                      return null;
                    });

                  /** Catching error in previous API call */
                  if (insertAccountingEntity) {
                    this._accountingEntitiesStore.data.push(
                      insertAccountingEntity
                    );
                  } else {
                    throw new Error(
                      "OninsertAccountingEntity - Error retriving accounting entity with id= " +
                      idAccountingEntity
                    );
                  }
                }
              } else if (operationKind === GenericOperationKind.UPDATE) {
                const indexUpdateEntity =
                  this._accountingEntitiesStore.data.findIndex(
                    (accountingEntity: AccountingEntities) =>
                      accountingEntity.id === idAccountingEntity
                  );

                /** Retrive update object from api and subribe it to mqtt logic */
                if (indexUpdateEntity >= 0) {
                  const updateAccountingEntity: AccountingEntities =
                    await this.GetAccountingEntityByID(
                      idAccountingEntity
                    ).catch((error) => {
                      return null;
                    });

                  /** Retrive update object from api */
                  if (updateAccountingEntity) {
                    this._accountingEntitiesStore.data[indexUpdateEntity] =
                      updateAccountingEntity;
                  } else {
                    throw new Error(
                      "OnupdateAccountingEntity - Error retriving object with id= " +
                      idAccountingEntity
                    );
                  }
                } else {
                  throw new Error(
                    "OnupdateAccountingEntity object not found id= " +
                    idAccountingEntity
                  );
                }
              } else if (operationKind === GenericOperationKind.DELETE) {
                this._accountingEntitiesStore.data =
                  this._accountingEntitiesStore.data.filter(
                    (entity: AccountingEntities) =>
                      entity.id !== idAccountingEntity
                  );
              }
              return idAccountingEntity + "|" + clientID;
            } finally {
              release();
            }
          } else {
            throw new Error(
              "OnAccountingEntityDataChange - Incorrect format message"
            );
          }
        }),
        debounceTime(1000)
      )
      .subscribe(
        (promiseIDAccountingEntity) => {
          promiseIDAccountingEntity.then(
            (IDAccountingEntityChanged_ClientID) => {
              if (IDAccountingEntityChanged_ClientID) {
                const values = IDAccountingEntityChanged_ClientID.split("|");

                /** Update store with object list changed and propagate modify */
                this._accountingEntitiesStore.lastAPIModified = moment().format(
                  MomentFormats.MOMENT_DATETIME_FORMAT
                );
                this._accountingEntitiesSubject.next(
                  this._accountingEntitiesStore.data
                );
                this._eventService.OnAccountingEntitiesChange.next(
                  IDAccountingEntityChanged_ClientID
                );
                this.setToStorage(this._accountingEntitiesStore);
              }
            }
          );
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            this._logContext,
            "subscribeMQTTAddresses()",
            "OnAccountingEntityChanged - incorrect message format"
          );
        }
      );

    await this.checkLocalAddressSubscriptionMap(
      mqttAddress,
      mqttAddressSubcription
    );
  }

  async checkLocalAddressSubscriptionMap(
    addressToCheck: string,
    addressSubscription: Subscription
  ): Promise<boolean> {
    let result: boolean = false;
    if (this._mqttAddressSubscriptionsMap.has(addressToCheck)) {
      addressSubscription.unsubscribe();

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "Address ALREADY present " + addressToCheck
      );
    } else {
      this._mqttAddressSubscriptionsMap.set(
        addressToCheck,
        addressSubscription
      );

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "checkLocalAddressSubscriptionMap()",
        "NEW address: " + addressToCheck
      );
      result = true;
    }
    return result;
  }

  async unsubscribeToMQTTAddresses(): Promise<void> {
    for (const entry of Array.from(
      this._mqttAddressSubscriptionsMap.entries()
    )) {
      entry[1].unsubscribe();

      this._mqttAddressSubscriptionsMap.delete(entry[0]);
    }
  }

  //#endregion

  //#region
  /**
   * Get object data from API. If api call fallback in error costructor, it set the appropriate flag "_isErrorInGetAccountingEntitiesListAPIRequest" to
   * TRUE, it set the store to NULL and emit on the osbservable a neutral value (IE. empty array).
   */
  GetAccountingEntitites(filter: string): Subscription {
    const queryString: string = "?" + filter;

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot +
        AppSettings.API_ACCOUNTING_ENTITY +
        queryString
      )
      .subscribe(
        (returnAccountingEntityStore: GenericApiData) => {
          //Subscribing mqtt accounting entities address

          this._isErrorInGetAccountingEntitiesListAPIRequest = false;

          this._accountingEntitiesStore = returnAccountingEntityStore;
          this._accountingEntitiesSubject.next(
            returnAccountingEntityStore.data
          );

          this.setToStorage(returnAccountingEntityStore);
        },
        (error) => {
          this.handlePeculiarApiResponseError(error);
          this._isErrorInGetAccountingEntitiesListAPIRequest = true;
          this._accountingEntitiesStore = null;
          this._accountingEntitiesSubject.next([]);
        }
      );
  }

  /**
   * Get accounting entity data from API with last modify data store.
   * If api call fallback in error costructor, it set the appropriate flag "_isErrorInGetAccountingEntityListAPIRequest" to
   * TRUE, it leaves the store as it is and emit on the osbservable a neutral value (IE. empty array).
   * If original data store isn't changed, api return nothin (code 200)
   */
  private GetAccountingEntitiesByLastModify(
    accountingEntitiesStore: GenericApiData
  ) {
    const queryString: string =
      "?lastModified=" +
      encodeURIComponent(accountingEntitiesStore.lastAPIModified);

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot +
        AppSettings.API_ACCOUNTING_ENTITY +
        queryString
      )
      .subscribe(
        (returnAccountingEntitiesStore: GenericApiData) => {
          this._isErrorInGetAccountingEntitiesListAPIRequest = false;

          if (returnAccountingEntitiesStore) {
            accountingEntitiesStore = returnAccountingEntitiesStore;

            this.setToStorage(accountingEntitiesStore);
          }

          this._accountingEntitiesStore = accountingEntitiesStore;

          this._accountingEntitiesSubject.next(accountingEntitiesStore.data);
        },
        (error) => {
          this.handlePeculiarApiResponseError(error);
          this._isErrorInGetAccountingEntitiesListAPIRequest = true;
          this._accountingEntitiesSubject.next([]);
        }
      );
  }

  /**
   * Get accounting entities data from API. If api call fallback in error costructor, it propagate an Observable notification with .error() function
   */
  private async GetAccountingEntityByID(idAccountingEntity: number) {
    const queryString: string = "?id=" + idAccountingEntity;

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot +
        AppSettings.API_ACCOUNTING_ENTITY +
        queryString
      )
      .pipe(
        switchMap((returnAccountingEntitiesList: GenericApiData) => {
          /** Clean API object from unnecessary data
           *  and check if data key is populated: if length is equal to zero, no
           *  accounting entity is finded
           */
          if (returnAccountingEntitiesList.data.length > 0) {
            const returnAccountingEntity: AccountingEntities =
              returnAccountingEntitiesList.data[0];

            return of(returnAccountingEntity);
          } else {
            return throwError(new Error());
          }
        })
      )
      .toPromise()
      .then(
        (returnAccountingEntities: AccountingEntities) => {
          return returnAccountingEntities;
        },
        (error) => {
          throw new Error();
        }
      );
  }

  /**
   * Generate payment note from list of entities and period
   * @param request
   * @returns response containing the ids of the accounting notes generated
   */
  public async GeneratePayment(request: AccountingRequest) {
    return this._http
      .post(
        environment.httpServerRoot + AppSettings.API_ACCOUNTING_ENTITY_GENERATE,
        request
      )
      .pipe(
        switchMap((returnAccountingGeneration: AccountingResult) => {
          return of(returnAccountingGeneration);
        })
      )
      .toPromise()
      .then(
        (returnAccountingEntities: AccountingResult) => {
          return returnAccountingEntities;
        },
        (error) => {
          throw new Error();
        }
      );
  }

  /**
   * Generate payment note from list of entities and period
   * @param request
   * @returns response containing the names of the file generated
   */
  public async GeneratePaymentFile(request: AccountingRequestFile) {
    return this._http
      .post(
        environment.httpServerRoot +
        AppSettings.API_ACCOUNTING_ENTITY_GENERATEFILE,
        request
      )
      .pipe(
        switchMap((returnAccountingGeneration: AccountingResult) => {
          return of(returnAccountingGeneration);
        })
      )
      .toPromise()
      .then(
        (returnAccountingEntities: AccountingResult) => {
          return returnAccountingEntities;
        },
        (error) => {
          throw new Error();
        }
      );
  }
  //#endregion

  //#region Storage
  protected getFromStorage(): Promise<object> {
    //get from storage and insert in local list of accounting entities
    return this._storageService.get(AppSettings.KEY_ACCOUNTING_ENTITIES).then(
      (accountingentityStorage: GenericApiData) => {
        if (accountingentityStorage) {
          return accountingentityStorage;
        } else {
          return null;
        }
      },
      (error) => {
        return null;
      }
    );
  }

  protected setToStorage(accountingEntitiesStore: GenericApiData) {
    this._storageService.set(
      AppSettings.KEY_ACCOUNTING_ENTITIES,
      accountingEntitiesStore
    );
  }
  //#endregion

  //#region Accounting notes
  public GetAccountingNotes(IDsAccountingNotes: string, filter: string) {
    const queryString: string = "?IDs=" + IDsAccountingNotes + "&" + filter;

    return this._http
      .get<GenericApiData>(
        environment.httpServerRoot +
        AppSettings.API_ACCOUNTING_NOTES +
        queryString
      )
      .pipe(
        switchMap((returnAccountingNotesList: GenericApiData) => {
          /** Clean API object from unnecessary data
           *  and check if data key is populated: if length is equal to zero, no
           *  accounting entity is finded
           */
          if (returnAccountingNotesList.data.length > 0) {
            const returnAccountingNotes: AccountingNote[] =
              returnAccountingNotesList.data;

            return of(returnAccountingNotes);
          } else {
            return throwError(new Error());
          }
        })
      )
      .toPromise()
      .then(
        (returnAccountingNotes: AccountingNote[]) => {
          return returnAccountingNotes;
        },
        (error) => {
          throw error;
        }
      );
  }
  //#endregion

  //#region Accounting notes
  public SaveAccountingNotes(IDsAccountingNotes: string) {
    let param: GenericIDs = new GenericIDs();
    param.Ids = IDsAccountingNotes.split(",").map((id) => parseInt(id));
    return this._http
      .post(
        environment.httpServerRoot + AppSettings.API_ACCOUNTING_NOTESSAVE,
        param
      )
      .toPromise()
      .then(
        (result: any) => {
          return result;
        },
        (error) => {
          throw new Error();
        }
      );
  }
  //#endregion

  publishAddress(mqttAddress: string, value: string) {
    this._mqttService.publishAddress(mqttAddress, value);
  }

  /**
   * Handle errors in api call which require peculiar reactions from the page; this includes cases where the error is propagated through subject.error()
   * @param error
   */
  private handlePeculiarApiResponseError(error: any) {
    switch (error.status) {
      /*This usually is associated with attempts by the app to require access to server-side features which are NOT currently supported*/
      case ApiErrorType.NOT_FOUND:
        this._isErrorNotSupported = true;
        break;
      default:
        break;
    }
  }
}
